import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo
} from 'react';
import Mux from '@mux/mux-player-react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { PhotographPropTypes } from 'dpl/shared/utils/customPropTypes';
import withLazyLoading from 'dpl/decorators/withLazyLoading';
import SmartImage from 'dpl/components/SmartImage';
import ClickableDiv from 'dpl/common/components/ClickableDiv';
import MediaCarouselSplide, {
  DEFAULT_SPLIDE_OPTIONS
} from 'dpl/common/components/MediaCarouselSplide';
import { pushParams } from 'dpl/actions/query_params';
import { isEmpty } from 'dpl/shared/utils/object';
import { sendAnalyticsEvent } from 'dpl/util/analytics';
import { ANALYTICS } from 'dpl/constants/query_params';
import { ANALYTICS_EVENTS } from 'dpl/constants/analytics';
import { openPuppyPage } from 'dpl/puppy_page/utils/helpers';
import { CAROUSEL_TYPES } from 'dpl/puppy_search_results/utils/constants';

import VideoIconOverlay from './VideoIconOverlay';

const COMMON_ARROW_CLASS_NAME = 'bg-white black f6';

const CAPTION_CLASS_NAME =
  'CardMediaCarouselFile__caption pv1 ph2 br2 bg-black-60 f1 fw-medium white truncate';

const WRAPPER_CLASS_NAME = 'CardMediaCarouselFile h-100 db relative';

const VideoContent = React.memo(
  /** @param {any} props */ function VideoContent({
    mediaFile,
    width = null,
    height = null,
    muxRef = null
  }) {
    const [isPlaying, setIsPlaying] = useState(false);

    return (
      <div className="CarouselMediaFile relative">
        <Mux
          ref={muxRef}
          playbackId={mediaFile.playback_ids?.[0]?.id}
          style={{
            '--bottom-controls': 'none',
            '--center-seek-forward-button': 'none',
            '--center-seek-backward-button': 'none',
            '--bottom-play-button': 'none',
            '--bottom-volume-range': 'none',
            '--bottom-time-range:': 'none',
            '--bottom-time-display': 'none',
            '--bottom-duration-display': 'none',
            '--bottom-mute-button': 'none',
            '--bottom-captions-button': 'none',
            '--bottom-airplay-button': 'none',
            '--bottom-pip-button': 'none',
            '--bottom-playback-rate-button': 'none',
            width,
            height
          }}
          preload="none"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
        <VideoIconOverlay
          className="absolute absolute--fill pointer-events-none z-1"
          isPlaying={isPlaying}
        />
      </div>
    );
  }
);

const ImageContent = React.memo(
  /** @param {any} props */ function ImageContent({
    smartImageProps,
    alt = '',
    url,
    caption = ''
  }) {
    return (
      <>
        <SmartImage {...smartImageProps} alt={alt} src={url} />
        {caption && (
          <div
            className="absolute top-0 pv1 ph1 right-0"
            style={
              smartImageProps?.width
                ? { maxWidth: smartImageProps.width }
                : undefined
            }
          >
            <p
              dangerouslySetInnerHTML={{ __html: caption }}
              className={CAPTION_CLASS_NAME}
            />
          </div>
        )}
      </>
    );
  }
);

const cardMediaCarouselFileDefaultPropAnalytics = {};
/** @param {any} props */
export function CardMediaCarouselFile({
  caption = null,
  mediaFile,
  smartImageProps,
  analytics = cardMediaCarouselFileDefaultPropAnalytics,
  muxRef = null
}) {
  const dispatch = useDispatch();

  const {
    alt,
    analytics: imageAnalytics,
    clickthrough_url: clickthroughUrl,
    url,
    type,
    dog_modal_id: dogModalId = {},
    id
  } = mediaFile;

  const hasDogModal = !isEmpty(dogModalId);
  const puppyId = dogModalId?.puppy_id;
  const isVideo = type === 'Video';

  const handleClick = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      if (isVideo) return;

      if (imageAnalytics) {
        sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, imageAnalytics);
      }

      if (puppyId) {
        openPuppyPage({ puppyId });
      } else if (hasDogModal) {
        dispatch(
          pushParams({ ...dogModalId, [ANALYTICS]: analytics }, { hash: true })
        );
      } else {
        window.location.href = clickthroughUrl;
      }
    },
    [
      isVideo,
      imageAnalytics,
      puppyId,
      hasDogModal,
      dogModalId,
      analytics,
      clickthroughUrl
    ]
  );

  const clickableDivStyle = useMemo(
    () => ({ width: smartImageProps.width }),
    [smartImageProps.width]
  );

  if (hasDogModal || clickthroughUrl || isVideo) {
    return (
      <ClickableDiv
        data-test-id={`card-media-carousel-${isVideo ? 'video' : 'image'}-${id}`}
        className={WRAPPER_CLASS_NAME}
        style={clickableDivStyle}
        onClick={handleClick}
      >
        {isVideo ? (
          <VideoContent
            mediaFile={mediaFile}
            width={smartImageProps.width}
            height={smartImageProps.height}
            muxRef={muxRef}
          />
        ) : (
          <ImageContent
            smartImageProps={smartImageProps}
            alt={alt}
            url={url}
            caption={caption}
          />
        )}
      </ClickableDiv>
    );
  }

  return (
    <div
      className={WRAPPER_CLASS_NAME}
      data-test-id={`card-media-carousel-image-${id}`}
    >
      <ImageContent
        smartImageProps={smartImageProps}
        alt={alt}
        url={url}
        caption={caption}
      />
    </div>
  );
}

VideoContent.propTypes = {
  mediaFile: PropTypes.shape({
    playback_ids: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired
      })
    )
  }).isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  muxRef: PropTypes.object
};

ImageContent.propTypes = {
  smartImageProps: PropTypes.object.isRequired,
  alt: PropTypes.string,
  url: PropTypes.string.isRequired,
  caption: PropTypes.string
};

CardMediaCarouselFile.propTypes = {
  caption: PropTypes.string,
  mediaFile: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    analytics: PropTypes.shape({
      view: PropTypes.string.isRequired,
      view_feature: PropTypes.string.isRequired,
      view_feature_location: PropTypes.string.isRequired
    }),
    url: PropTypes.string,
    alt: PropTypes.string,
    clickthrough_url: PropTypes.string,
    playback_ids: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        policy: PropTypes.string.isRequired
      })
    ),
    status: PropTypes.string,
    aspect_ratio: PropTypes.string,
    type: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    dog_modal_id: PropTypes.object
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  smartImageProps: PropTypes.object.isRequired,
  analytics: PropTypes.shape({
    position: PropTypes.number,
    view: PropTypes.string
  }),
  // eslint-disable-next-line react/forbid-prop-types
  muxRef: PropTypes.object
};

const cardMediaCarouselDefaultPropMediaFiles = [];
const cardMediaCarouselDefaultPropAnalytics = {};
const CardMediaCarousel = React.memo(
  /** @param {any} props */ function CardMediaCarousel({
    mediaFiles = cardMediaCarouselDefaultPropMediaFiles,
    smartImageProps,
    isInViewport,
    setLazyRef = null,
    showCaptions = false,
    isPuppyCard = false,
    analytics = cardMediaCarouselDefaultPropAnalytics
  }) {
    const carouselRef = useRef(null);
    const splideRef = useRef(null);
    const [rightPadding, setRightPadding] = useState(0);
    const [perPage, setPerPage] = useState(1);

    // horizontal padding between carousel items
    const fileSpacing = isPuppyCard ? 0 : 2;

    const splideOptions = {
      ...DEFAULT_SPLIDE_OPTIONS,
      perPage,
      padding: { left: fileSpacing, right: rightPadding },
      ...(isPuppyCard && {
        type: CAROUSEL_TYPES.SLIDE,
        flickPower: 250,
        easing: 'cubic-bezier(0.42, 0, 0.58, 1)'
      })
    };
    useEffect(() => {
      const { offsetWidth } = carouselRef.current;
      const fileWidth = parseInt(smartImageProps.width, 10);
      const fileWidthWithSpacing = fileWidth + fileSpacing;

      // sometimes we can fit multiple media files in one page...whatcha gonna do!
      const filesPerPage = Math.floor(offsetWidth / fileWidthWithSpacing);
      if (filesPerPage > 1) {
        setPerPage(filesPerPage);
      }

      // HACK: Subtracting 1 here will ensure there is no preview of the previous file
      const padding =
        offsetWidth - fileWidthWithSpacing * filesPerPage - fileSpacing - 1;
      setRightPadding(padding || 0);
    }, []);

    return (
      <div
        className={classnames('CardMediaCarousel', { ml4: !isPuppyCard })}
        ref={carouselRef}
      >
        <div
          style={{
            minHeight: smartImageProps.height || '1px',
            minWidth: smartImageProps.width || '1px'
          }}
          ref={setLazyRef}
        >
          {isInViewport && (
            <MediaCarouselSplide
              splideClassName="BreederSearchResultsPhoto h-100 f0"
              leftArrowClassName={classnames(COMMON_ARROW_CLASS_NAME, {
                ml8: !isPuppyCard,
                ml4: isPuppyCard
              })}
              rightArrowClassName={classnames(COMMON_ARROW_CLASS_NAME, 'mr4')}
              mediaFiles={mediaFiles}
              ref={splideRef}
              CarouselMediaComponent={CardMediaCarouselFile}
              smartImageProps={smartImageProps}
              splideOptions={splideOptions}
              showCaptions={showCaptions}
              hasPhotoBullets={isPuppyCard}
              analytics={analytics}
              bulletStyle={{ transform: 'scale(0.85)', margin: '2px' }}
            />
          )}
        </div>
      </div>
    );
  }
);

CardMediaCarousel.propTypes = {
  mediaFiles: PropTypes.arrayOf(
    PropTypes.shape({
      ...PhotographPropTypes,
      url: PropTypes.string,
      urls: PropTypes.arrayOf(PropTypes.string)
    })
  ),
  // eslint-disable-next-line react/forbid-prop-types
  smartImageProps: PropTypes.object.isRequired,
  showCaptions: PropTypes.bool,
  isInViewport: PropTypes.bool.isRequired,
  setLazyRef: PropTypes.func,
  isPuppyCard: PropTypes.bool,
  analytics: PropTypes.shape({
    position: PropTypes.number,
    view: PropTypes.string
  })
};

export default withLazyLoading({
  defaultLazy: true
})(CardMediaCarousel);
